if (!customElements.get('spec-kit-wizard')) {
  customElements.define(
    'spec-kit-wizard',
    class SpecKitWizard extends HTMLElement {
      connectedCallback() {
        this.catalog = window.P4SpecCatalog;
        if (!this.catalog) {
          this.showStatus('Could not load the parts catalog. Call (916) 783-0747.');
          return;
        }
        this.products = [];
        this.vehicle = null;
        this.generation = null;
        this.slots = this.catalog.emptySlots();
        this.phone = this.getAttribute('data-phone') || '(916) 783-0747';
        this.statusEl = this.querySelector('[data-status]');
        this.builderEl = this.querySelector('[data-builder]');
        this.addButton = this.querySelector('[data-add]');
        this.resetButtons = this.querySelectorAll('[data-reset]');
        this.errorEl = this.querySelector('[data-error]');
        this.bind();
        this.load();
      }

      bind() {
        this.addEventListener('click', (event) => {
          const choice = event.target.closest('[data-choose]');
          if (choice) {
            this.choose(choice.getAttribute('data-choose'), choice.getAttribute('data-value'));
            return;
          }
          if (event.target.closest('[data-reset]')) {
            this.reset();
            return;
          }
          const card = event.target.closest('[data-product]');
          if (card && !card.disabled) {
            const id = Number(card.getAttribute('data-product'));
            const product = this.products.find((item) => item.variantId === id);
            if (product) {
              this.slots = this.catalog.selectProduct(this.slots, product);
              this.render();
            }
          }
        });
        this.addButton.addEventListener('click', () => this.addBuild());
      }

      showStatus(message) {
        this.statusEl.hidden = !message;
        this.statusEl.textContent = message || '';
      }

      async load() {
        const handles = JSON.parse(this.getAttribute('data-collections') || '[]');
        try {
          const groups = await Promise.all(handles.map((handle) => this.fetchCollection(handle)));
          this.products = this.catalog.classifyCatalog(groups.flat());
          if (!this.products.length) {
            this.showStatus(`No matching parts. Call ${this.phone}.`);
            return;
          }
          this.showStatus('');
          this.render();
        } catch (error) {
          this.showStatus(`Could not load parts. Call ${this.phone}.`);
        }
      }

      async fetchCollection(handle) {
        const products = [];
        for (let page = 1; page <= 20; page++) {
          const response = await fetch(`/collections/${handle}/products.json?limit=250&page=${page}`);
          if (!response.ok) throw new Error('collection');
          const data = await response.json();
          if (!data.products || !data.products.length) break;
          products.push(...data.products);
        }
        return products;
      }

      reset() {
        const next = this.catalog.resetWizardState
          ? this.catalog.resetWizardState()
          : { vehicle: null, generation: null, slots: this.catalog.emptySlots() };
        this.vehicle = next.vehicle;
        this.generation = next.generation;
        this.slots = next.slots;
        if (this.errorEl) {
          this.errorEl.hidden = true;
          this.errorEl.textContent = '';
        }
        this.render();
      }

      choose(key, value) {
        if (key === 'vehicle') {
          this.vehicle = value;
          this.generation = null;
          this.slots = this.catalog.emptySlots();
        } else if (key === 'generation') {
          this.generation = value;
          this.slots = this.catalog.emptySlots();
        }
        this.render();
      }

      money(amount) {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
          parseFloat(amount)
        );
      }

      render() {
        const vehicles = this.catalog.vehiclesIn(this.products);
        this.renderChips(
          'vehicle',
          vehicles.map((item) => ({ value: item.id, label: item.label })),
          this.vehicle
        );
        this.toggle('vehicle', true);

        const generations = this.vehicle ? this.catalog.generationsIn(this.products, this.vehicle) : [];
        if (this.vehicle && generations.length === 1 && !this.generation) {
          this.generation = generations[0].id;
        }
        this.toggle('generation', Boolean(this.vehicle));
        this.renderChips(
          'generation',
          generations.map((item) => ({ value: item.id, label: item.label })),
          this.generation
        );

        const ready = Boolean(this.vehicle && this.generation);
        this.builderEl.hidden = !ready;
        this.resetButtons.forEach((button) => {
          const inSummary = Boolean(button.closest('[data-summary]'));
          button.hidden = !this.vehicle || (inSummary ? !ready : ready);
        });
        if (!ready) {
          this.catalog.SLOTS.forEach((slot) => {
            const root = this.querySelector(`[data-slot="${slot}"]`);
            if (!root) return;
            root.hidden = true;
            const cards = root.querySelector('[data-cards]');
            if (cards) cards.replaceChildren();
            const includedEl = root.querySelector('[data-included]');
            if (includedEl) {
              includedEl.hidden = true;
              includedEl.textContent = '';
            }
          });
          this.renderSummary();
          return;
        }

        this.catalog.SLOTS.forEach((slot) => {
          this.renderSlot(slot);
        });
        this.renderSummary();
      }

      toggle(step, show) {
        this.querySelector(`[data-step="${step}"]`).hidden = !show;
      }

      renderChips(key, items, selected) {
        const list = this.querySelector(`[data-step="${key}"] [data-choices]`);
        list.replaceChildren();
        items.forEach((item) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'p4-spec__choice';
          button.setAttribute('data-choose', key);
          button.setAttribute('data-value', item.value);
          button.setAttribute('aria-pressed', selected === item.value ? 'true' : 'false');
          button.textContent = item.label;
          list.appendChild(button);
        });
      }

      renderSlot(slot) {
        const root = this.querySelector(`[data-slot="${slot}"]`);
        const options = this.catalog.optionsForSlot(
          this.products,
          this.vehicle,
          this.generation,
          slot,
          this.slots
        );
        const current = this.slots[slot];
        const included = Boolean(current && current.included);
        if (!options.length && !included) {
          root.hidden = true;
          return;
        }
        root.hidden = false;
        root.querySelector('[data-slot-title]').textContent = this.catalog.SLOT_LABELS[slot];
        const includedEl = root.querySelector('[data-included]');
        if (included) {
          includedEl.hidden = false;
          includedEl.textContent = `Included with ${current.product.title}`;
        } else {
          includedEl.hidden = true;
          includedEl.textContent = '';
        }
        const cards = root.querySelector('[data-cards]');
        cards.replaceChildren();
        if (included) return;
        options.forEach((product) => {
          cards.appendChild(this.cardEl(product, current && current.variantId === product.variantId));
        });
      }

      cardEl(product, selected) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'p4-spec__card';
        button.setAttribute('data-product', String(product.variantId));
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        if (!product.available) {
          button.disabled = true;
          button.classList.add('is-sold-out');
        }
        const img = document.createElement('img');
        img.alt = product.title;
        img.width = 144;
        img.height = 144;
        if (product.image) img.src = product.image;
        const body = document.createElement('span');
        body.className = 'p4-spec__card-body';
        const brand = document.createElement('span');
        brand.className = 'p4-spec__card-brand caption';
        brand.textContent = product.vendor;
        const title = document.createElement('span');
        title.className = 'p4-spec__card-title';
        title.textContent = product.label || product.title;
        const full = document.createElement('span');
        full.className = 'p4-spec__card-full caption';
        full.textContent = product.title;
        body.append(brand, title, full);
        const price = document.createElement('span');
        price.className = 'p4-spec__card-price';
        price.textContent = product.available ? this.money(product.price) : 'Sold out';
        button.append(img, body, price);
        return button;
      }

      selectedLines() {
        const seen = new Set();
        const lines = [];
        this.catalog.SLOTS.forEach((slot) => {
          const entry = this.slots[slot];
          if (!entry || entry.included || seen.has(entry.variantId)) return;
          seen.add(entry.variantId);
          lines.push(entry.product);
        });
        return lines;
      }

      renderSummary() {
        const lines = this.selectedLines();
        const list = this.querySelector('[data-lines]');
        const empty = this.querySelector('[data-empty]');
        const subtotalEl = this.querySelector('[data-subtotal]');
        list.replaceChildren();
        empty.hidden = lines.length > 0;
        subtotalEl.hidden = lines.length === 0;
        this.addButton.disabled = lines.length === 0;
        if (!lines.length) {
          subtotalEl.textContent = '';
          return;
        }
        let total = 0;
        lines.forEach((product) => {
          total += parseFloat(product.price) || 0;
          const item = document.createElement('li');
          item.className = 'p4-spec__line';
          const name = document.createElement('a');
          name.href = product.url;
          name.textContent = product.title;
          const price = document.createElement('span');
          price.textContent = this.money(product.price);
          item.append(name, price);
          list.appendChild(item);
        });
        subtotalEl.textContent = this.money(total);
      }

      cartSectionsComplete(response, sectionIds) {
        return Boolean(
          response &&
            response.sections &&
            sectionIds.every((id) => typeof response.sections[id] === 'string' && response.sections[id].length)
        );
      }

      ensureCartSections(response, sectionIds) {
        if (!sectionIds.length || this.cartSectionsComplete(response, sectionIds)) {
          return Promise.resolve(response);
        }
        const params = new URLSearchParams({ sections: sectionIds.join(',') });
        const url = `${window.location.pathname}?${params.toString()}`;
        return fetch(url, { headers: { Accept: 'application/json' } })
          .then((res) => res.json())
          .then((sections) => Object.assign({}, response, { sections }))
          .catch(() => response);
      }

      addBuild() {
        const lines = this.selectedLines();
        if (!lines.length) return;
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const formData = new FormData();
        lines.forEach((product, index) => {
          formData.append(`items[${index}][id]`, String(product.variantId));
          formData.append(`items[${index}][quantity]`, '1');
        });
        const sectionIds = cart ? cart.getSectionsToRender().map((section) => section.id) : [];
        if (cart) {
          formData.append('sections', sectionIds.join(','));
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement(document.activeElement);
        }

        this.addButton.setAttribute('aria-disabled', 'true');
        this.errorEl.hidden = true;

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        fetch(window.routes.cart_add_url, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.errorEl.hidden = false;
              this.errorEl.textContent = response.description || response.message || 'Could not add to cart.';
              return null;
            }
            if (!cart) {
              window.location = window.routes.cart_url;
              return null;
            }
            if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
            return this.ensureCartSections(response, sectionIds).then((withSections) => {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'spec-kit',
                productVariantId: lines[0].variantId,
                cartData: withSections,
              });
              if (this.cartSectionsComplete(withSections, sectionIds)) {
                cart.renderContents(withSections);
              } else {
                window.location = window.routes.cart_url;
              }
            });
          })
          .catch(() => {
            this.errorEl.hidden = false;
            this.errorEl.textContent = `Could not add to cart. Call ${this.phone}.`;
          })
          .finally(() => {
            this.addButton.removeAttribute('aria-disabled');
          });
      }
    }
  );
}
